/**
 * OverviewPanel — on-chart trend dashboard (Round 92).
 *
 * Vertical panel pinned to the chart's top-left (below the OHLC legend). Shows
 * two stacked sections — chart timeframe and its mapped Higher Timeframe — plus
 * an FNO read, each with the consolidated -100..+100 score and one colored shape
 * per indicator. Read-only.
 *
 * 2026-09-02 REDESIGN — same information, roughly half the height.
 * The panel was ~200x640 and near-opaque: it punched a hole in the price action
 * for its whole height, overflowed its pane in quad layout, and three stacked
 * 132px speedometers accounted for ~400px of that for three numbers. The
 * speedometers are now compact ScoreMeters (~26px each), the surface is properly
 * frosted so candles read through, and the panel is height-constrained so it can
 * never overflow its pane. Nothing was removed: all three scores, all 14
 * indicators and every FNO value are still here.
 */
import React from 'react';
import { Box, Typography, Tooltip, Divider } from '@mui/material';
import { CH_TYPE, chOverlaySurface, chNumeric, chHairline } from '../../styles/appUi';
import {
  TimeframeTrends, OVERVIEW_INDICATORS, INDICATOR_LABELS, OverviewIndicatorKey,
} from '../../utils/overviewTrends';
import { OverviewZone, TrendDir, zoneForScore, ZONE_LABELS } from '../../theme/overviewColors';

export interface OverviewSettings {
  zoneColors: Record<OverviewZone, string>;
  upColor: string;
  downColor: string;
  neutralColor: string;
  strongThreshold: number;
  probableThreshold: number;
  gaugeOpacity: number;
}

interface Props {
  tf: TimeframeTrends | null;
  htf: TimeframeTrends | null;
  tfLabel: string;
  htfLabel: string;
  ov: OverviewSettings;
  isDark: boolean;
  loadingHtf?: boolean;
  /** Per-constituent weights — a constituent with weight ≤ 0 is OFF (not feeding
   *  the score) and is shown dimmed + struck-through so it's clear at a glance
   *  which indicators are currently driving the OverView score. */
  weights?: Partial<Record<OverviewIndicatorKey, number>>;
  /** FNO outlook (futures + options bias) — shown as a section when the symbol has
   *  options/futures derived data. Null = hidden. */
  fno?: FnoOutlook | null;
  /** Composite FNO Bull/Bear score (-100..+100) at the live spot — drives the FNO
   *  speedometer in the FNO section. Null = no meter. */
  fnoScore?: number | null;
}

export interface FnoOutlook {
  supported: boolean;
  options: {
    pcr: number; maxPain: number | null; spot: number; gexTotal: number; regime: string;
    bias: string; reasons: string[];
    dteHours?: number;
    gexFlip?: number | null;
    nearestPosWall?: number | null;
    nearestNegWall?: number | null;
    topSupport?: number | null;
    topResistance?: number | null;
    flow?: { score: number; bias: string } | null;
    pinRisk?: { level: string; strike: number; dteHours: number } | null;
  } | null;
  futures: { oiPattern: string | null; basis: number; bias: string; reasons: string[] } | null;
  combined: { bias: string; score: number };
}

const GLYPH: Record<TrendDir, string> = { up: '▲', down: '▼', neutral: '◆', na: '–' };

const Shape: React.FC<{ dir: TrendDir; ov: OverviewSettings }> = ({ dir, ov }) => {
  const color = dir === 'up' ? ov.upColor : dir === 'down' ? ov.downColor : dir === 'neutral' ? ov.neutralColor : '#78909c';
  return (
    <Box component="span" sx={{ color, fontSize: CH_TYPE.micro, lineHeight: 1, fontWeight: 700, width: 11, textAlign: 'center', flexShrink: 0, opacity: dir === 'na' ? 0.5 : 1 }}>
      {GLYPH[dir]}
    </Box>
  );
};

/**
 * ScoreMeter — the consolidated -100..+100 score as a horizontal track with a
 * centre tick and a marker, plus the number and its zone on the same line.
 *
 * Replaces a 132px-wide, ~110px-tall speedometer. Three of those stacked was
 * ~400px of chart height spent on three numbers; this reads the same value at a
 * glance in ~26px, and a linear track actually compares better across the three
 * sections than three separate dials do.
 */
const ScoreMeter: React.FC<{
  score: number; zone: OverviewZone; ov: OverviewSettings; label: string; isDark: boolean;
}> = ({ score, zone, ov, label, isDark }) => {
  const col = ov.zoneColors[zone] || ov.neutralColor;
  const clamped = Math.max(-100, Math.min(100, score));
  // 0 -> 50%. Marker and fill share this mapping so they can never disagree.
  const pct = (clamped + 100) / 2;
  const track = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(15,23,42,0.10)';
  return (
    <Box sx={{ px: 0.2 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.25 }}>
        <Typography sx={{ fontSize: CH_TYPE.micro, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.3, whiteSpace: 'nowrap' }}>
          {label}
        </Typography>
        <Typography noWrap sx={{ fontSize: CH_TYPE.micro, color: col, fontWeight: 700, ml: 'auto', textTransform: 'uppercase', letterSpacing: 0.2, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {ZONE_LABELS[zone]}
        </Typography>
        <Typography sx={{ fontSize: CH_TYPE.body, fontWeight: 800, color: col, ...chNumeric, flexShrink: 0 }}>
          {clamped > 0 ? '+' : ''}{Math.round(clamped)}
        </Typography>
      </Box>
      {/* Track: bar grows from the centre toward the score, so direction and
          magnitude are both readable without reading the number. */}
      <Box sx={{ position: 'relative', height: 6, borderRadius: 3, bgcolor: track, overflow: 'hidden' }}>
        <Box sx={{
          position: 'absolute', top: 0, bottom: 0, bgcolor: col, opacity: ov.gaugeOpacity ?? 1,
          left: clamped >= 0 ? '50%' : `${pct}%`,
          width: `${Math.abs(clamped) / 2}%`,
          transition: 'left 180ms ease, width 180ms ease',
        }} />
        <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '1px', bgcolor: isDark ? 'rgba(255,255,255,0.30)' : 'rgba(15,23,42,0.28)' }} />
      </Box>
    </Box>
  );
};

const Section: React.FC<{ trends: TimeframeTrends | null; label: string; ov: OverviewSettings; isDark: boolean; loading?: boolean; weights?: Partial<Record<OverviewIndicatorKey, number>> }> = ({ trends, label, ov, isDark, loading, weights }) => {
  if (!trends) {
    return (
      <Box sx={{ py: 0.6, display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
        <Typography sx={{ fontSize: CH_TYPE.micro, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.3 }}>{label}</Typography>
        <Typography sx={{ fontSize: CH_TYPE.micro, color: 'text.disabled', ml: 'auto' }}>{loading ? 'loading…' : 'no data'}</Typography>
      </Box>
    );
  }
  return (
    <Box>
      <ScoreMeter score={trends.score} zone={trends.zone} ov={ov} label={label} isDark={isDark} />
      {/* R120 — two-column grid so the 14 constituents fit a compact height.
          The glyph leads its label (it used to trail it), because with two
          columns a trailing glyph sits right next to the NEXT column's label
          and reads as belonging to it. A leading glyph is unambiguous, and a
          hairline between the columns settles it completely. */}
      <Box sx={{ mt: 0.4, display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 0.9, rowGap: 0 }}>
        {OVERVIEW_INDICATORS.map((k: OverviewIndicatorKey, i: number) => {
          const t = trends.perIndicator[k];
          const off = (weights?.[k] ?? 1) <= 0;
          const leftCol = i % 2 === 0;
          return (
            <Tooltip key={k} title={off ? `${INDICATOR_LABELS[k]} — OFF (not feeding the score)` : t.detail} arrow placement="top">
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.4, py: 0, cursor: 'default',
                minWidth: 0, opacity: off ? 0.34 : 1,
                ...(leftCol ? { borderRight: `1px solid ${chHairline(isDark)}`, pr: 0.9 } : {}),
              }}>
                <Shape dir={off ? 'na' : t.dir} ov={ov} />
                <Typography noWrap sx={{ fontSize: CH_TYPE.micro, color: 'text.secondary', lineHeight: 1.55, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: off ? 'line-through' : 'none' }}>{INDICATOR_LABELS[k]}</Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

const FnoSection: React.FC<{ fno: FnoOutlook; ov: OverviewSettings; fnoScore?: number | null; isDark: boolean }> = ({ fno, ov, fnoScore, isDark }) => {
  const col = (b: string) => b === 'BULLISH' ? ov.upColor : b === 'BEARISH' ? ov.downColor : ov.neutralColor;
  const row = (label: string, bias: string, detail: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.45, py: 0.08 }}>
      <Typography sx={{ fontSize: CH_TYPE.micro, fontWeight: 700, color: 'text.secondary', width: 30, flexShrink: 0 }}>{label}</Typography>
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: col(bias), flexShrink: 0 }} />
      <Typography noWrap sx={{ fontSize: CH_TYPE.micro, color: 'text.secondary', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', ...chNumeric }}>{detail}</Typography>
    </Box>
  );
  const opt = fno.options, fut = fno.futures;
  return (
    <>
      <Divider sx={{ my: 0.6 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.35 }}>
        <Typography sx={{ fontSize: CH_TYPE.micro, fontWeight: 800, letterSpacing: 0.4, color: 'text.secondary' }}>FNO OUTLOOK</Typography>
        <Box sx={{ ml: 'auto', px: 0.6, py: 0.1, borderRadius: 0.6, bgcolor: col(fno.combined.bias), color: '#fff', fontSize: CH_TYPE.micro, fontWeight: 800, lineHeight: 1.4 }}>{fno.combined.bias}</Box>
      </Box>
      {fnoScore != null && (
        <Box sx={{ mb: 0.4 }}>
          <ScoreMeter
            score={Math.round(fnoScore)}
            zone={zoneForScore(fnoScore, ov.strongThreshold, ov.probableThreshold)}
            ov={ov}
            label="FNO"
            isDark={isDark}
          />
        </Box>
      )}
      {fut && row('FUT', fut.bias, `${(fut.oiPattern || '—').replace('_', ' ').toLowerCase()}${fut.basis ? ` · basis ${fut.basis.toFixed(2)}` : ''}`)}
      {opt && row('OPT', opt.bias, `PCR ${opt.pcr ? opt.pcr.toFixed(2) : '—'}${opt.maxPain ? ` · MP ${Math.round(opt.maxPain)}` : ''}`)}
      {opt?.flow && row('FLOW', opt.flow.bias, `ΔOI near spot · OVI ${opt.flow.score >= 0 ? '+' : ''}${opt.flow.score.toFixed(2)}`)}
      {opt && (
        <Tooltip arrow title={opt.regime}>
          <Typography sx={{ fontSize: CH_TYPE.micro, color: 'text.disabled', textAlign: 'center', mt: 0.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
            γ: {opt.gexTotal >= 0 ? 'long-γ (range)' : 'short-γ (trend)'}{opt.dteHours != null ? ` · ${opt.dteHours < 24 ? `${opt.dteHours}h` : `${Math.round(opt.dteHours / 24)}d`} to exp` : ''}
          </Typography>
        </Tooltip>
      )}
      {/* Key levels — GEX flip vs spot, nearest γ walls, top OI S/R. */}
      {opt && (opt.gexFlip != null || opt.nearestPosWall != null || opt.topSupport != null || opt.topResistance != null) && (
        <Box sx={{ mt: 0.2 }}>
          {opt.gexFlip != null && (
            <Typography sx={{ fontSize: CH_TYPE.micro, color: 'text.disabled', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              flip {Math.round(opt.gexFlip)} · spot {opt.spot >= opt.gexFlip ? 'above (range)' : 'below (trend)'}
            </Typography>
          )}
          {(opt.nearestPosWall != null || opt.nearestNegWall != null) && (
            <Typography sx={{ fontSize: CH_TYPE.micro, color: 'text.disabled', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              γ walls <Box component="span" sx={{ color: ov.upColor }}>+{opt.nearestPosWall != null ? Math.round(opt.nearestPosWall) : '—'}</Box> / <Box component="span" sx={{ color: ov.downColor }}>−{opt.nearestNegWall != null ? Math.round(opt.nearestNegWall) : '—'}</Box>
            </Typography>
          )}
          {(opt.topSupport != null || opt.topResistance != null) && (
            <Typography sx={{ fontSize: CH_TYPE.micro, color: 'text.disabled', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              OI <Box component="span" sx={{ color: ov.upColor }}>S {opt.topSupport != null ? Math.round(opt.topSupport) : '—'}</Box> · <Box component="span" sx={{ color: ov.downColor }}>R {opt.topResistance != null ? Math.round(opt.topResistance) : '—'}</Box>
            </Typography>
          )}
        </Box>
      )}
      {opt?.pinRisk && opt.pinRisk.level !== 'LOW' && (
        <Typography sx={{ fontSize: CH_TYPE.micro, textAlign: 'center', fontWeight: 700, color: opt.pinRisk.level === 'HIGH' ? ov.downColor : ov.neutralColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          ⊙ Pin risk {opt.pinRisk.level} @ {opt.pinRisk.strike}
        </Typography>
      )}
    </>
  );
};

const OverviewPanel: React.FC<Props> = ({ tf, htf, tfLabel, htfLabel, ov, isDark, loadingHtf, weights, fno, fnoScore }) => {
  return (
    <Box sx={{
      width: 208,
      px: 0.9, py: 0.7,
      ...chOverlaySurface(isDark),
      pointerEvents: 'auto',
      /* Never taller than the pane it floats in. In quad layout the panel used
         to run past the bottom of its pane and get clipped mid-sentence. The
         wrapper in ChartsPage is anchored top AND bottom so it has a definite
         height for this percentage to resolve against. */
      maxHeight: '100%',
      overflowY: 'auto',
      overflowX: 'hidden',
      '&::-webkit-scrollbar': { width: 5 },
      '&::-webkit-scrollbar-thumb': {
        borderRadius: 3,
        backgroundColor: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(15,23,42,0.18)',
      },
    }}>
      <Typography sx={{ fontSize: CH_TYPE.micro, fontWeight: 800, letterSpacing: 0.6, color: 'text.secondary', mb: 0.5 }}>
        OVERVIEW
      </Typography>
      <Section trends={tf} label={tfLabel} ov={ov} isDark={isDark} weights={weights} />
      <Divider sx={{ my: 0.6 }} />
      <Section trends={htf} label={`HTF · ${htfLabel}`} ov={ov} isDark={isDark} loading={loadingHtf} weights={weights} />
      {fno?.supported && (fno.options || fno.futures) && <FnoSection fno={fno} ov={ov} fnoScore={fnoScore} isDark={isDark} />}
    </Box>
  );
};

export default OverviewPanel;
